import { Component, Inject, Input, Output, EventEmitter } from '@angular/core';
import { FormControl } from '@angular/forms';

import { BehaviorSubject, combineLatest, forkJoin, Observable, of } from 'rxjs';
import { debounceTime, filter, mergeMap } from 'rxjs/operators';

import { JSR310ConversionService } from "../../../../shared/jsr310-localdates/services/jsr310-conversion.service";
import { LocalDateTime } from '../../../../shared/jsr310-localdates/types/local-date-time.type';

import { ActeOnMService } from "../../../actes-onm/services/actes-onm.service";
import { ActeOnMEntity } from '../../../actes-onm/entities/actes-onm-entity';

import { ParcTLR } from '../../../structure/parc/entities/parc-tlr.entity';

import { OuvrageDto } from '../../../structure/ouvrage/entities/ouvrage-dto.entity';

import { AccesService } from "../services/acces.service";

import { OperationCarl } from '../entities/operation-carl.entity';
import { AccesOperation } from '../entities/acces-operation.entity';
import { AccesDemandeIntervention } from '../entities/acces-demande-intervention.entity';
import { AccesBindedElement } from '../entities/acces-binded-element';
import { WorkOrder } from '../entities/work-order.entity';
import { Notification } from '../entities/notification.entity';

type ActRef = {
    [acteId: number]: ActeOnMEntity;
}

type PendingOperations = {
    [key: string]: boolean;
}

export type ActeOperations = { 
    [id: number]: AccesOperation[]; 
}

type ActeVisibilities = {
    [acteId: number]: boolean;
}

type DemandeVisibilities = {
    [demandeRef: string]: boolean;
}

export class AccesDto {
    id!: number;
	numero!: string;
	ouvragesDto!: OuvrageDto[];
	etat!: number;
	accesCegex!: number;
	actesOperationsAssociees: ActeOperations = {};
	dateDebut!: LocalDateTime;
	pendingOperations!: AccesOperation[];
	associatedDIs: AccesDemandeIntervention[] = [];
	associatedWorkOrders: WorkOrder[] = [];
	associatedNotifications: Notification[] = [];
	type: number = 0;
	ice!: boolean;
	parc!: ParcTLR;
}

@Component({
	selector: 'acces-operation-binder',
	templateUrl: 'acces-operation-binder.html',
	styleUrls: ['acces-operation-binder.css']
})
export class AccesOperationBinderComponent {
	// Le composant de saisie pour le filtre, vu comme un
	// FormControl, pour manipulation simplifie de son contenu
	filterInput = new FormControl();
	
	// Valeurs bindees sur la vue
	accesRef!: AccesDto;
	actes: ActeOnMEntity[] = [];
	workorders: WorkOrder[] = [];
	notifications: Notification[] = [];
	pendingOperations: PendingOperations = {};
	foldWorkOrderPannel: boolean = true;
	foldNotificationPannel: boolean = true;
	acteVisible: ActeVisibilities = {};
	parc: ParcTLR | null = null;
	
	shouldShowWoPannel: () => boolean = () => {
		if (this.filterInput.value) {
			return this.workorders.some($ => this.shouldShowWorkOrder($));
		}
		return true;
	};

	shouldShowNotifPannel: () => boolean = () => {
		if (this.filterInput.value) {
			return this.notifications.some($ => this.shouldShowNotif($));
		}
		return true;
	};

	shouldShowNotif: (notification: Notification) => boolean = (notification: Notification) => {
		const searchCriteria: string = this.filterInput.value;
		if (searchCriteria && searchCriteria.length) {
			const matchCriteria = (notification.notificationDesc + '|' + notification.notificationNumber + '|' + notification.status + '|' + notification.statusText).toLocaleLowerCase().indexOf(searchCriteria.toLowerCase()) >= 0;
			const alreadyAssociated = this.accesRef.associatedNotifications.some( notif => notif.notificationNumber === notification.notificationNumber );
			return matchCriteria || alreadyAssociated;
		}
		return true;
	};
	
	shouldShowWorkOrder: (wo: WorkOrder) => boolean = (wo: WorkOrder) => {
		const searchCriteria: string = this.filterInput.value;
		if (searchCriteria && searchCriteria.length) {
			const matchCriteria = (wo.workOrderDesc + '|' + wo.workOrder + '|' + wo.status + '|' + wo.statusText).toLocaleLowerCase().indexOf(searchCriteria.toLowerCase()) >= 0;
			const alreadyAssociated = this.accesRef.associatedWorkOrders.some( $ => $.workOrder === wo.workOrder );
			return matchCriteria || alreadyAssociated;
		}
		return true;
	};

	private referentielDesActes: ActRef = {};
	private acces$ = new BehaviorSubject<AccesDto>(new AccesDto());
	
	constructor(
		@Inject(AccesService) accesService: AccesService,
		@Inject(ActeOnMService) acteOnMService: ActeOnMService,
		@Inject(JSR310ConversionService) jsr310ConversionService: JSR310ConversionService
	) {
		// A la reception d'un nouvel acces
		let accesData$ = combineLatest([this.acces$]).pipe(
			// Ignorer le cas ou on recoit un acces sans id
			// Cela peut arriver dans le cas ou la vue parente s'initialise et que
			// le moteur de binding envoie l'Input 'acces' avant qu'il ne soit charge
			filter(([acces]) => acces.id !== undefined),
			// Charger les informatiosn reliees a cet acces
			mergeMap(
				([acces]) => {
					
					let notifications$: Observable<Notification[]> = accesService.searchNotifications(acces.id);
					let workorders$: Observable<WorkOrder[]> = accesService.searchWorkOrders(acces.id);
					let actes$ = acteOnMService.getActesSelonAcces(acces.id);
					
					return forkJoin({
						acces: of(acces),
						actes: actes$,
						workorders: workorders$,
						notifications: notifications$
					});

				}
			)
		);
		
		// Une fois les informations obtenues du serveur
		accesData$.subscribe(
			({ acces, actes, workorders, notifications }) => {
				
				// Binder l'acces sur la vue
				this.accesRef = acces;
				
				if (acces.parc) {
				  this.parc = acces.parc;
				}
			
				// Reinitialiser le champ de filtre
				this.filterInput.setValue(null);
				// autres reinitialisations
				this.foldWorkOrderPannel = true;
				this.foldNotificationPannel = true;
				
				// Binder les actes sur la vue, et preparer l'arborescence
				this.actes = actes;
				this.onAfterChargementActes.emit(this.actes.length > 0 || workorders.length > 0 || notifications.length > 0);
				this.foldAll();
				
				this.pendingOperations = {};
				this.actes = this.actes.filter( acte => acte.id !== -1 );

				// indiquer pour quels ouvrages de l'acces on a des pending operations
				acces.pendingOperations.forEach(pendingOperation => {
					this.pendingOperations[pendingOperation.acteOnmId + '#' + pendingOperation.ouvrageId] = true;
					if (!acces.actesOperationsAssociees[pendingOperation.acteOnmId]) {
						acces.actesOperationsAssociees[pendingOperation.acteOnmId] = [];
					}
					let operation = new OperationCarl(pendingOperation.ouvrageId, pendingOperation.codeActe, acces.dateDebut, pendingOperation.acteOnmId);
					operation.refOuvrageDansCarl = pendingOperation.boxLocation;
					acces.actesOperationsAssociees[pendingOperation.acteOnmId].push(
						new AccesOperation(acces.id, pendingOperation.acteOnmId, true, operation, '', 0, pendingOperation.ouvrageNom, pendingOperation.codeActe)
					);
					this.unfoldByNoeudId(pendingOperation.acteOnmId);
				});

				// Rendre tous ces actes visibles par defaut
				this.acteVisible = {};
				this.forceVisibility(this.actes, true);
				
				// binder les wo et notifs à la vue
				this.workorders = workorders;
				acces.associatedWorkOrders = [];
				workorders.forEach( wo => {
					if (wo.alreadyBound) {
						acces.associatedWorkOrders.push(wo);
					}
				} );
				this.foldWorkOrderPannel = acces.associatedWorkOrders.length == 0;
				
				this.notifications = notifications;
				acces.associatedNotifications = [];
				notifications.forEach( notif => {
					if (notif.alreadyBound) {
						acces.associatedNotifications.push(notif);
					}
				} );
				this.foldNotificationPannel = acces.associatedNotifications.length == 0;
				
				acces.associatedDIs = [];
				
				if (acces.pendingOperations.length === 0 && acces.associatedWorkOrders.length === 0 && acces.associatedNotifications.length === 0) {
					this.unfoldAll();
				}
				

				// Initialisation des binders dans la sortie
				[this.onBindingOperation, this.onBindingWorkOrder, this.onBindingNotification].forEach(
					(emitter) => emitter.emit(
							new AccesBindedElement(
								acces.id,
								acces.associatedDIs,
								acces.actesOperationsAssociees,
								acces.associatedWorkOrders,
								acces.associatedNotifications
							)
					)
				);
			}
		)
		
		// Programmation de la reaction aux saisies dans le champs de filtre
		this.filterInput.valueChanges.pipe(
			// Ignorer la valeur "null" => ce n'est pas une saisie utilisateur
			// Elle n'est utilisee que pour initaliser le champ
			filter(value => value !== null),
			// Ignorer les saisies qui ont lieu a moins de 500ms d'intervale
			debounceTime(500),
		).subscribe(
			(value: string) => this.evaluateFilter(value)
		);
	}
	
	// Entree permettant au parent de transmettre l'acces concerne
	// (Cette action provoque l'initialisation du composant)
	@Input() set acces(accesDto: AccesDto) {
		if (accesDto) {
			this.acces$.next(accesDto);
		}
	}
	
	@Input() set aucunActeAssociable(value: boolean) {
	    if (value) {
	        this.uncheckAll();
	    }
	}

	// Evenement permettant au parent de reagir apres un changement de model
	@Output() onBindingOperation = new EventEmitter<AccesBindedElement>();
	@Output() onBindingWorkOrder = new EventEmitter<AccesBindedElement>();
	@Output() onBindingNotification = new EventEmitter<AccesBindedElement>();
	@Output() hasSelectionChange = new EventEmitter<boolean>();

	// Evenement permettant au parent de reagir quand on a trouvé des actes
	@Output() onAfterChargementActes = new EventEmitter<boolean>();
	
	////////////////////////////////////////////////////
	// Fonctions internes de gestion de l'arborescence
	
	private evaluateFilter(filter: string) {
		this.acteVisible = {};
		if (filter === "") {
			this.forceVisibility(this.actes, true);
		} else {
			let normalizedFilter = this.normalizeValue(filter);
			this.evaluateVisibilityOfActes(normalizedFilter, this.actes)
		}
	}
	
	private evaluateVisibilityOfActes(normalizedFilter: string, actes: ActeOnMEntity[]): boolean {
		let someVisible = false;
		for (let acte of actes) {
			let isCategory = acte.children && acte.children.length > 0;
			
			let visible = false;
			if (isCategory) {
				// Pour les categories

				// Evaluer immediatement si le label matche
				let normalizedLabel = this.normalizeValue(acte.libelle);
				let labelMatches = normalizedLabel.includes(normalizedFilter);
		
				if (labelMatches) {
					// Pour une categorie dont le label matche:
					
					// ... la rendre visible
					visible = true;
					
					// ... et d'assurer de meme que tous ces descendants sont visibles
					this.forceVisibility(acte.children, false);
				} else {
					// Pour une categorie qui ne matche pas, la visibilite depend
					// de si elle a des enfants visibles
					visible = this.evaluateVisibilityOfActes(normalizedFilter, acte.children);
				}
			} else {
				// Normalement cet attribut n'a pas d'influence pour une feuille, mais
				// par souci de proprete, on indique que la feuille ne peut pas etre repliee
				
				// Evaluer si l'acte feuille doit etre visible
				visible = this.isLeafActeVisibleWithFilter(acte, normalizedFilter);
			}

			// Binder la visibilite sur la vue
			this.acteVisible[acte.id] = visible;

			// Deplier/replier en fonction de cette visibilite (cf retour de recette evol 50648)
			acte.folded = !visible;
			
			if (visible) {
				someVisible = true;
			}			
		}
		
		return someVisible;
	}
	
	private isLeafActeVisibleWithFilter(acte: ActeOnMEntity, normalizedFilter: string): boolean {
		// Tout d'abord, regarder si l'acte comporte un acte ou au moins une suggestion de creation
		let test = this.checkContent(acte);
		if (!test.hasContent) {
			// Masquer la feuille si il n'y a ni suggestion ni operation
			return false;
		}
		
		// Ensuite, verifier s'il y a au moins une suggestion ou operation cochee pour l'acte
		if (test.hasChecks) {
			// Forcer l'affichage s'il y a en a une cochee
			return true;
		}
		
		// Si on a echappe a tous ces cas speciaux, on compare le label au filtre
		let normalizedLabel = this.normalizeValue(acte.libelle);
		let labelMatches = normalizedLabel.includes(normalizedFilter);
		
		return labelMatches;
	}
	
	private checkContent(acte: ActeOnMEntity): { hasContent: boolean, hasChecks: boolean, hasOperations: boolean } {
		let noSuggestion = this.noneSuggested(acte);
		if (noSuggestion) {
			// Masquerla feuille si il n'y a ni suggestion ni operation
			return { hasContent: false, hasChecks: false, hasOperations: false };
		}
		
		// Ensuite, verifier s'il y a au moins une suggestion cochee pour l'acte
		if (!noSuggestion) {
			let someSuggestionChecked = this.someSuggestionChecked(acte);
			if (someSuggestionChecked) {
				return { hasContent: true, hasChecks: true, hasOperations: false };
			}
		}
		
		return { hasContent: true, hasChecks: false, hasOperations: false };
	}
	
	private noneSuggested(acte: ActeOnMEntity): boolean {
		// Verifier pour chacun des ouvrages
		for (let ouvrage of this.accesRef.ouvragesDto) {
			// Si une suggestion de creation de nouvelle operation doit etre proposee
			// pour cet ouvrage et cet acte
			if (this.shouldSuggestCreation(acte, ouvrage)) {
				return false;
			}
		}
		return true;
	}
	
	private someSuggestionChecked(acte: ActeOnMEntity): boolean {
		// Verifier pour chacun des ouvrages
		for (let ouvrage of this.accesRef.ouvragesDto) {
			// Si une suggestion de creation de nouvelle operation doit etre proposee
			// pour cet ouvrage et cet acte
			if (this.shouldSuggestCreation(acte, ouvrage)) {
				// Et si c'est le cas, est-elle cochee actuellement
				if (this.pendingOperations[acte.id + '#' + ouvrage.id]) {
					return true;
				}
			}
		}
		return false;
	}
	
	private forceVisibility(actes: ActeOnMEntity[], folded: boolean): boolean {
		let someUnfolded = !folded;
		for (let acte of actes) {
			this.acteVisible[acte.id] = true;
			
			if (folded) {
				// Dans le cas ou on est cense replier tous les noeuds
				
				// Selon si c'est une categorie ou pas
				if (acte.children && acte.children.length > 0) {
					// Categorie: on replie seulement si aucun enfant n'est lui-meme deplie
					let someChildrenUnfolded = this.forceVisibility(acte.children, folded);
					acte.folded = !someChildrenUnfolded;
					if (someChildrenUnfolded) {
						someUnfolded = true;
					}
				} else {
					// Feuille
					
					// Tester le contenu de la feuille
					let test = this.checkContent(acte);
					
					// Ne replier comme demande que si rien est coche dedans
					acte.folded = !test.hasChecks;
					if (test.hasChecks) {
						someUnfolded = true;
					}
				}
			} else {
				// Dans le cas ou on est censee deplier tous les noeuds
				// Le faire tout simplement
				acte.folded = folded;
				if (acte.children && acte.children.length > 0) {
					this.forceVisibility(acte.children, folded);
				}
			}
		}
		
		return someUnfolded;
	}

	// Normalise un string pour comparaison sans etre genee par la casse ou certains caracteres speciaux
	private normalizeValue(value: string | undefined): string {
		if (!value) {
			return '';
		}
		return value.toLowerCase().replace(/\s/g, '');
	}
	
	private unfoldByNoeudId(id: number) {
		this.actes.forEach( acte => {
			this.unfoldArborescence(acte, id);
		} );
	}
	
	private unfoldArborescence(acte: ActeOnMEntity, id: number) {
		if (acte.id === id) {
			acte.folded = false;
			if (acte.parentId && this.referentielDesActes[acte.parentId]) {
				 // on ouvre le parent
				this.unfoldByNoeudId(acte.parentId);
			}
		} else if (acte.children){
			acte.children.forEach( child => {
				this.unfoldArborescence(child, id);
			} );
		}
	}
	
	private fold(acte: ActeOnMEntity) {
		if (acte.children) {
			acte.children.forEach(child => {
				this.fold(child);
			});
		}
		acte.folded = true;
		this.referentielDesActes[acte.id] = acte;
	}
	
	private unfold(acte: ActeOnMEntity) {
		if (acte.children) {
			acte.children.forEach(child => {
				this.unfold(child);
			});
		}
		acte.folded = false;
	}
	
	private toggleActe(acte: ActeOnMEntity, id: number) {
		if (acte.id === id) {
			acte.folded = !acte.folded;
		} else if (acte.children){
			acte.children.forEach( child => {
				this.toggleActe(child, id);
			} );
		}
	}
	
	// Fonction utilisee en tant que RDG pour un point d'affichae
	shouldSuggestCreation(acte: ActeOnMEntity, ouvrage: OuvrageDto) {
		return true;
	}
	
	//////////////////////////////////////////////////
	// Fonctions de reactions aux actions utilisateur
	
	foldAll() {
		 // on profite de ce parcours recursif pour renseigner un referentiel des actes
		this.referentielDesActes = {};
		this.actes.forEach(acte => this.fold(acte) );
	}
	
	unfoldAll() {
		this.actes.forEach(acte => this.unfold(acte) );
	}
	
	toggle(id: number) {
		this.actes.forEach(
			acte => {
				this.toggleActe(acte, id);
			}
		);
	}
	
	onCheckNew(value: boolean, acte: ActeOnMEntity, ouvrage: OuvrageDto) {
		this.onCheckActe(value, acte, ouvrage);
	}
	
	onCheckActe(value: boolean, acte: ActeOnMEntity, ouvrage: OuvrageDto){
		let acces: AccesDto = this.acces$.getValue();
		if (value) {
			if (!acces.actesOperationsAssociees[acte.id]) {
				acces.actesOperationsAssociees[acte.id] = [];
			}
			let aop = {
				accesId: acces.id,
				ouvrageId: ouvrage.id,
				acteOnmId: acte.id,
				createdByAurora: true
			} as AccesOperation;
			acces.actesOperationsAssociees[acte.id].push(aop);
		} else {
			if (acces.actesOperationsAssociees[acte.id]) {
				acces.actesOperationsAssociees[acte.id] = acces.actesOperationsAssociees[acte.id].filter(association => {
					return association.ouvrageId !== ouvrage.id;
				});
			}
		}
		this.onBindingOperation.emit(new AccesBindedElement(
			acces.id,
			acces.associatedDIs,
			acces.actesOperationsAssociees,
			acces.associatedWorkOrders,
			acces.associatedNotifications
		));
		
		this.hasSelectionChange.emit(this.hasAnySelection());
	}
	
	onCheckExistingOperation(value: boolean, acte: ActeOnMEntity, ouvrage: OuvrageDto, operation: OperationCarl) {
		let acces: AccesDto = this.acces$.getValue();
		if (value) {
			console.log('on veut associer l\'operation: ', operation , ' de type: ', acte.code , ' sur l\'ouvrage: ', ouvrage.nom);
			if (!acces.actesOperationsAssociees[acte.id]) {
				acces.actesOperationsAssociees[acte.id] = [];
			}
			acces.actesOperationsAssociees[acte.id].push(
				new AccesOperation(acces.id, acte.id, false, operation, '', 0, ouvrage.nom, acte.code)
			);
		} else {
			console.log('on veut plus associer l\'operation: ', operation , ' de type: ', acte.code , ' sur l\'ouvrage: ', ouvrage.nom);
			if (acces.actesOperationsAssociees[acte.id]) {
				acces.actesOperationsAssociees[acte.id] = 
				acces.actesOperationsAssociees[acte.id].filter( $ => $.codeCarl !== operation.refOperationCarl );
			}
		}
		this.onBindingOperation.emit(new AccesBindedElement(
			acces.id,
			acces.associatedDIs,
			acces.actesOperationsAssociees,
			acces.associatedWorkOrders,
			acces.associatedNotifications
		));
		
		this.hasSelectionChange.emit(this.hasAnySelection());
	}
	
	onCheckWorkOrder(_value: boolean, _wo: WorkOrder) {
		let acces = this.acces$.getValue();
		let accesId = acces.id;
		let workorders = this.workorders
			.filter(wo => wo.alreadyBound)
		;
		let notifications = this.notifications
			.filter(n => n.alreadyBound)
		;
		this.onBindingWorkOrder.emit(
			new AccesBindedElement(
				accesId,
				acces.associatedDIs,
				acces.actesOperationsAssociees,
				workorders,
				notifications
			)
		);
		
		this.hasSelectionChange.emit(this.hasAnySelection());
	}

	onCheckNotification(_value: boolean, _notif: Notification) {
		let acces = this.acces$.getValue();
		let accesId = acces.id;
		let notifications = this.notifications
			.filter(n => n.alreadyBound)
		;
		let workorders = this.workorders
			.filter(wo => wo.alreadyBound)
		;
		this.onBindingNotification.emit(
			new AccesBindedElement(
				accesId,
				acces.associatedDIs,
				acces.actesOperationsAssociees,
				workorders,
				notifications
			)
		);
		
		this.hasSelectionChange.emit(this.hasAnySelection());
	}
	
	private uncheckAll() {

	    Object.keys(this.pendingOperations).forEach(key => {
	        this.pendingOperations[key] = false;
	    });

	    this.workorders.forEach(wo => wo.alreadyBound = false);

	    this.notifications.forEach(n => n.alreadyBound = false);

	    const acces = this.acces$.getValue();
	    acces.actesOperationsAssociees = {};
	    acces.associatedDIs = [];
	    acces.associatedWorkOrders = [];
	    acces.associatedNotifications = [];

	    const payload = new AccesBindedElement(
	        acces.id,
	        [],
	        {},
	        [],
	        []
	    );

	    this.onBindingOperation.emit(payload);
	    this.onBindingWorkOrder.emit(payload);
	    this.onBindingNotification.emit(payload);
	}
	
	private hasAnySelection(): boolean {
	    return (
			Object.values( this.pendingOperations || {} ).some( v => v ) ||
	        this.workorders.some( wo => wo.alreadyBound ) ||
	        this.notifications.some( n => n.alreadyBound )
	    );
	}
}